/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AgentConnectionResponse } from '../models/AgentConnectionResponse';
import type { CreateAgentParams } from '../models/CreateAgentParams';
import type { GeneratePortalThreadSignatureParams } from '../models/GeneratePortalThreadSignatureParams';
import type { GenerateThreadSignatureParams } from '../models/GenerateThreadSignatureParams';
import type { User } from '../models/User';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ChatAgentService {

    /**
     * Retrieve Agent Access Token
     * Returns the agent access token and URLs
     * @param accountSlug Account Slug
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgentFetchAccessToken(
        accountSlug: string,
    ): CancelablePromise<{
        access_token: string;
        chat_agent_connection_url: string;
        chat_agent_widget_url: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent/fetch_access_token',
            query: {
                'account_slug': accountSlug,
            },
        });
    }

    /**
     * Create ATS Connection
     * @param accountSlug Account Slug
     * @param brandId Brand Id
     * @param enableInFountainPlatforms Enable agent in Fountain platform
     * @param smsToChatAgentEnabled Enable agent for sms conversation
     * @param id Agent Id
     * @param status Agent Status
     * @returns void
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgentPublishChatAgent(
        accountSlug: string,
        brandId?: string,
        enableInFountainPlatforms?: boolean,
        smsToChatAgentEnabled?: boolean,
        id?: string,
        status?: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent/publish_chat_agent',
            query: {
                'account_slug': accountSlug,
                'brand_id': brandId,
                'enable_in_fountain_platforms': enableInFountainPlatforms,
                'sms_to_chat_agent_enabled': smsToChatAgentEnabled,
                'id': id,
                'status': status,
            },
        });
    }

    /**
     * Generate Thread Signature
     * @param accountSlug Account Slug
     * @param brandId Brand Id
     * @param requestBody Applicant
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgentThreadSignature(
        accountSlug: string,
        brandId: string,
        requestBody: GeneratePortalThreadSignatureParams,
    ): CancelablePromise<{
        widget_uri: string;
        agent_id: string;
        thread_id: string;
        signature: string;
        expiration: string;
        tenant: string;
        server: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent/thread_signature',
            query: {
                'account_slug': accountSlug,
                'brand_id': brandId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Generate Thread Signature
     * @param accountSlug Account Slug
     * @param funnelId Funnel Id
     * @param applicantId Applicant Id
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgentApplicantThreadSignature(
        accountSlug: string,
        funnelId: string,
        applicantId: string,
    ): CancelablePromise<{
        widget_uri: string;
        agent_id: string;
        signature: string;
        expiration: string;
        tenant: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent/applicant_thread_signature',
            query: {
                'account_slug': accountSlug,
                'funnel_id': funnelId,
                'applicant_id': applicantId,
            },
        });
    }

    /**
     * Generate WX I9 Bot Thread Signature
     * @param accountSlug Account Slug
     * @param brandId Brand Id
     * @param requestBody Applicant
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgentWxI9BotThreadSignature(
        accountSlug: string,
        brandId: string,
        requestBody: GenerateThreadSignatureParams,
    ): CancelablePromise<{
        widget_uri: string;
        agent_id: string;
        thread_id: string;
        signature: string;
        expiration: string;
        tenant: string;
        server: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent/wx_i9_bot_thread_signature',
            query: {
                'account_slug': accountSlug,
                'brand_id': brandId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Delete Agent
     * @param accountSlug Account Slug
     * @param id Agent ID
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiAgentIntegrationsAgent(
        accountSlug: string,
        id: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/agent_integrations/agent/{id}',
            path: {
                'id': id,
            },
            query: {
                'account_slug': accountSlug,
            },
        });
    }

    /**
     * Get Agent
     * @param accountSlug Account Slug
     * @param id Agent ID
     * @returns AgentConnectionResponse successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgent(
        accountSlug: string,
        id: string,
    ): CancelablePromise<AgentConnectionResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent/{id}',
            path: {
                'id': id,
            },
            query: {
                'account_slug': accountSlug,
            },
        });
    }

    /**
     * Update Agent
     * @param accountSlug Account Slug
     * @param id Agent ID
     * @param requestBody Agent Data
     * @param brandId Brand Id
     * @returns AgentConnectionResponse successful
     * @throws ApiError
     */
    public static putInternalApiAgentIntegrationsAgent(
        accountSlug: string,
        id: string,
        requestBody: {
            enable_in_fountain_platforms?: boolean;
            sms_to_chat_agent_enabled?: boolean;
        },
        brandId?: string,
    ): CancelablePromise<AgentConnectionResponse> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/internal_api/agent_integrations/agent/{id}',
            path: {
                'id': id,
            },
            query: {
                'account_slug': accountSlug,
                'brand_id': brandId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Create Agent
     * @param accountSlug Account Slug
     * @param requestBody Agent
     * @returns any created
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgent(
        accountSlug: string,
        requestBody: CreateAgentParams,
    ): CancelablePromise<{
        agent_id: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent',
            query: {
                'account_slug': accountSlug,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * list agent data
     * Lists the account's Candidate AI agents to account admins. `agents` comes from fountain-ai; when that call fails, `agents` is empty, `agents_error` names the failure, and `available_agents` still lists the agents Hire knows.
     * @param accountSlug Account Slug
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgent1(
        accountSlug: string,
    ): CancelablePromise<{
        sample_knowledge_base_file_url: string;
        knowledge_base_help_center_article_url: string;
        available_agents: Array<{
            agent_id: string;
            brand_name: string;
            has_active_agent: boolean;
        }>;
        agents: Array<{
            uuid: string;
            name: string;
            status: 'draft' | 'active' | 'disabled' | null;
            is_deleted: boolean;
            created_at: string | null;
            updated_at: string | null;
            /**
             * External id of the user who created the agent
             */
            created_by: string | null;
            /**
             * External id of the user who last changed the agent
             */
            last_modified_by: string | null;
            /**
             * Name of the last modifier, or of the creator when nobody changed it since
             */
            modified_by_name: string | null;
            knowledge_base_uuid: string | null;
        }>;
        /**
         * Set when fountain-ai did not return the agent list; `agents` is then empty
         */
        agents_error: 'missing_credentials' | 'auth_failed' | 'timeout' | 'server_error' | 'request_failed' | null;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent',
            query: {
                'account_slug': accountSlug,
            },
        });
    }

    /**
     * Create Rx Agent
     * @param accountSlug Account Slug
     * @returns any created
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgentCreateRxAgent(
        accountSlug: string,
    ): CancelablePromise<{
        rx_agent_id: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent/create_rx_agent',
            query: {
                'account_slug': accountSlug,
            },
        });
    }

    /**
     * Create Rx Thread and Signature
     * @param accountSlug Account Slug
     * @param id Agent ID
     * @param rxAgentId Rx Agent ID
     * @param knowledgeBaseId Knowledge Base ID
     * @returns any successful
     * @throws ApiError
     */
    public static postInternalApiAgentIntegrationsAgentCreateRxThreadAndSignature(
        accountSlug: string,
        id: string,
        rxAgentId: string,
        knowledgeBaseId: string,
    ): CancelablePromise<{
        thread_id: string;
        signature: string;
        expiration: string;
        tenant: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/agent_integrations/agent/create_rx_thread_and_signature',
            query: {
                'account_slug': accountSlug,
                'id': id,
                'rx_agent_id': rxAgentId,
                'knowledge_base_id': knowledgeBaseId,
            },
        });
    }

    /**
     * fetch user
     * Return user data
     * @param accountSlug Account Slug
     * @param userExternalId User external id
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgentUser(
        accountSlug: string,
        userExternalId: string,
    ): CancelablePromise<{
        user: User;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent/user',
            query: {
                'account_slug': accountSlug,
                'user_external_id': userExternalId,
            },
        });
    }

    /**
     * Search accessible Candidate AI conversation openings
     * Requires reports access. Returns up to 50 accessible openings ordered by title. Shares the conversation-log rate limit.
     * @param id AX agent UUID
     * @param accountSlug Selected account slug
     * @param q Literal title search; at least two characters after stripping whitespace
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgentConversationOpenings(
        id: string,
        accountSlug: string,
        q?: string,
    ): CancelablePromise<{
        openings: Array<{
            external_id: string;
            title: string;
        }>;
        sees_all_openings: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent/{id}/conversation_openings',
            path: {
                'id': id,
            },
            query: {
                'account_slug': accountSlug,
                'q': q,
            },
            errors: {
                403: `reports access denied`,
                404: `AX agent not connected to this account`,
                422: `query shorter than two characters or not a string`,
            },
        });
    }

    /**
     * Get a Candidate AI conversation transcript
     * Requires reports access. Returns a bounded transcript under the reader’s opening scope, display-only rich steps, and an applicant link only when the reader can access the applicant. Shares the conversation-log limit of 60 requests per minute per user and selected account.
     * @param id AX agent UUID
     * @param threadId Conversation thread UUID
     * @param accountSlug Selected account slug
     * @param turn Canonical turn UUID to highlight. An absent or unknown turn selects the newest messages.
     * @param opening Selected opening external ID. Narrows a restricted reader’s scope before applying the opening ceiling.
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgentConversationThread(
        id: string,
        threadId: string,
        accountSlug: string,
        turn?: string,
        opening?: string,
    ): CancelablePromise<{
        thread: {
            uuid: string;
            applicant_name: string | null;
            is_visitor: boolean;
            is_anonymized: boolean;
            opening: {
                external_id: string;
                title: string;
            } | null;
            channel: 'widget' | 'sms' | 'email' | 'whatsapp' | null;
            first_turn_at: string | null;
        };
        applicant_link: {
            account_slug: string;
            funnel_external_id: string;
            stage_external_id: string;
            applicant_external_id: string;
        } | null;
        /**
         * Opening display metadata keyed by opening UUID.
         */
        openings: Record<string, {
            title: string;
            location: string | null;
        }>;
        turns: Array<{
            turn_id: string;
            highlighted: boolean;
            thumbs_down: boolean;
            knowledge_gap: boolean;
            messages: Array<{
                id: string;
                role: 'system' | 'user' | 'assistant' | 'tool' | 'function';
                created_at: string;
                part: ({
                    type: 'text';
                    text: string;
                } | null | {
                    type: 'placeholder';
                    step_name: string;
                } | {
                    type: 'rich';
                    step: ({
                        kind: 'openings';
                        message: string | null;
                        opening_ids: Array<string>;
                    } | {
                        kind: 'openings_nearby' | 'application_form' | 'application_submitted';
                        message: string | null;
                    } | {
                        kind: 'question';
                        message: string | null;
                        question: string | null;
                    } | {
                        kind: 'other';
                        tool_name: string;
                    });
                });
            }>;
        }>;
        truncated_before: boolean;
        truncated_after: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent/{id}/conversations/{thread_id}',
            path: {
                'id': id,
                'thread_id': threadId,
            },
            query: {
                'account_slug': accountSlug,
                'turn': turn,
                'opening': opening,
            },
            errors: {
                403: `thread opening access denied or reports access denied`,
                404: `agent or thread not found in this account`,
                422: `invalid turn query shape or too many openings`,
                502: `conversation log unavailable`,
            },
        });
    }

    /**
     * List Candidate AI conversation turns
     * Requires reports access. Returns up to 50 turns under the reader’s opening scope. Limited to 60 requests per minute per user and selected account.
     * @param id AX agent UUID
     * @param accountSlug Selected account slug
     * @param from Inclusive UTC bound; required with cursor
     * @param to Exclusive UTC bound; required with cursor
     * @param cursor Reuse the preceding page’s window with this cursor
     * @param opening Opening external ID, or none for unrestricted readers. An accessible ID bypasses the opening ceiling.
     * @param channel
     * @param thumbsDown Only true enables the thumbs-down filter
     * @param knowledgeGap Only true enables the knowledge-gap filter
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiAgentIntegrationsAgentConversations(
        id: string,
        accountSlug: string,
        from?: string,
        to?: string,
        cursor?: string,
        opening?: string,
        channel?: 'widget' | 'sms' | 'whatsapp' | 'email',
        thumbsDown?: boolean,
        knowledgeGap?: boolean,
    ): CancelablePromise<{
        agent: {
            uuid: string;
            name: string;
            brand: string | null;
        };
        window: {
            from: string;
            to: string;
        };
        next_cursor: string | null;
        turns: Array<{
            id: string;
            thread_id: string;
            asked_at: string;
            applicant: {
                kind: 'applicant' | 'visitor' | 'anonymized';
                name: string | null;
            };
            opening: {
                id: string;
                title: string | null;
            } | null;
            channel: string | null;
            question: string | null;
            answered: boolean;
            thumbs_down: boolean;
            knowledge_gap: boolean;
            reply: Array<({
                type: 'text';
                text: string;
            } | {
                type: 'rich';
                step: {
                    kind: 'openings' | 'openings_nearby' | 'application_form' | 'question' | 'application_submitted' | 'other';
                    message?: string | null;
                    question?: string | null;
                    tool_name?: string;
                    openings?: Array<{
                        id: string;
                        title: string | null;
                        location: string | null;
                    }>;
                };
            })>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/agent_integrations/agent/{id}/conversations',
            path: {
                'id': id,
            },
            query: {
                'account_slug': accountSlug,
                'from': from,
                'to': to,
                'cursor': cursor,
                'opening': opening,
                'channel': channel,
                'thumbs_down': thumbsDown,
                'knowledge_gap': knowledgeGap,
            },
            errors: {
                403: `reports access denied`,
                404: `AX agent not connected to this account`,
                422: `invalid window, cursor, opening or channel, or too many openings`,
                429: `conversation-log rate limit exceeded`,
                502: `conversation log unavailable`,
            },
        });
    }

}